import {CssBaseline, ThemeProvider, useMediaQuery} from '@mui/material';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {Center, themeDark, themeLight, themePink} from '@resurs-internal/mui';
import {
  analytics,
  decodeAccessToken,
  isRestError,
  useThemeMode,
} from '@resurs-internal/rb-app-api';
import {createSyncStoragePersister} from '@tanstack/query-sync-storage-persister';
import {
  DehydrateOptions,
  MutationCache,
  QueryCache,
  QueryClient,
  useIsRestoring,
} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {PersistQueryClientProvider} from '@tanstack/react-query-persist-client';
import {AlertDialogProvider, ResursLoading} from 'components';
import React, {
  PropsWithChildren,
  ReactNode,
  Suspense,
  useEffect,
  useRef,
} from 'react';
import {AppSnackbarProvider} from './services/snackbar';
import {DialogProvider} from './hooks/useDialog/DialogProvider';
import {captureException, ErrorBoundary} from '@sentry/react';
import {ErrorPage} from 'pages';
import {auth} from '@resurs-internal/rb-app-api/features/auth/auth';
import {assetManager} from '@resurs-internal/rb-app-api/features/assets/assetManager';
import {AppContextProvider} from '@resurs-internal/rb-app-api/providers/context/AppContextProvider';
import {installQueryFocusListener} from '@resurs-internal/rb-app-api/services/queryFocus';
import {AppRouter} from './AppRouter';
import {BaseDialogImageMask} from '@resurs-internal/mui/components/BaseDialog/BaseDialogImageMask';
import * as Sentry from '@sentry/react';

const onError = (error: Error) => {
  if (!isRestError(error)) return captureException(error);

  const fingerprint = [`${error.status} ${error.method} ${error.url}`];
  captureException(error, {
    fingerprint: fingerprint,
  });
};

// Garbage collection time
const gcTime = 1000 * 60 * 10; // 10 minutes
const staleTime = 1000 * 60 * 5; // 5 minutes
const queryClient = new QueryClient({
  queryCache: new QueryCache({onError}),
  mutationCache: new MutationCache({onError}),
  defaultOptions: {
    queries: {
      gcTime,
      staleTime,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});

export const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

const dehydrateOptions: DehydrateOptions = {
  shouldDehydrateQuery: (query) => {
    // do not persist if local storage is not available
    if (window.localStorage === null) return false;
    // persist unless explicitly disabled
    return query?.meta?.persist !== false;
  },
};

// set auth provider storage adapter to session storage to enforce logout on tab close
auth.setStorage(sessionStorage);
assetManager.setStorage(localStorage);

// teaches react-query's focus manager about bfcache restores (pagehide/pageshow),
// which `visibilitychange` alone misses on Safari
installQueryFocusListener();

// set sentry user context
const setSentryUserContext = (token: string | undefined) => {
  if (!token) return Sentry.setUser(null);
  const partyId = decodeAccessToken(token)?.partyId;
  if (partyId) Sentry.setUser({id: partyId});
  else Sentry.setUser(null);
};

// hydrate auth state from storage
auth.hydrateAuth().then((state) => {
  setSentryUserContext(state.token);
});

// subscribe to auth state changes to sentry context
auth.subscribe((state) => {
  if (state.isLoggedIn) assetManager.initAssets();
  setSentryUserContext(state.token);
});

export const App = () => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{persister, dehydrateOptions}}>
      <AppContextProvider storage={localStorage}>
        <PersistGate
          fallback={
            <Center>
              <ResursLoading />
            </Center>
          }>
          <RBThemeProvider>
            <ErrorBoundary
              fallback={({error, componentStack, eventId}) => (
                <ErrorPage
                  error={error}
                  componentStack={componentStack}
                  eventId={eventId}
                />
              )}>
              <AppSnackbarProvider>
                <AlertDialogProvider>
                  <DialogProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Suspense
                        fallback={
                          <Center>
                            <ResursLoading />
                          </Center>
                        }>
                        <AppRouter />
                      </Suspense>
                    </LocalizationProvider>
                  </DialogProvider>
                </AlertDialogProvider>
              </AppSnackbarProvider>
            </ErrorBoundary>
          </RBThemeProvider>
        </PersistGate>
      </AppContextProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  );
};

const PersistGate: React.FC<PropsWithChildren<{fallback: ReactNode}>> = ({
  children,
  fallback,
}) => {
  const isRestoring = useIsRestoring();

  return <>{isRestoring ? fallback : children}</>;
};

const RBThemeProvider: React.FC<React.PropsWithChildren> = ({children}) => {
  const deviceTheme = useMediaQuery('(prefers-color-scheme: dark)')
    ? 'dark'
    : 'light';
  const themeMode = useThemeMode();
  const chosenTheme =
    themeMode?.value === 'device' ? deviceTheme : themeMode?.value;
  const theme = getTheme(chosenTheme);
  const bootSentRef = useRef(false);

  useEffect(() => {
    if (!bootSentRef.current) {
      analytics.click({
        name: `theme_status_web_${chosenTheme}`,
        pageId: 'web_startup',
      });
      bootSentRef.current = true;
    }
  }, [chosenTheme]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BaseDialogImageMask />
      {children}
    </ThemeProvider>
  );
};

const getTheme = (mode: 'dark' | 'light' | 'pink') => {
  if (mode === 'pink') {
    return themePink;
  }
  if (mode === 'light') {
    return themeLight;
  }
  if (mode === 'dark') {
    return themeDark;
  }
  return themeLight;
};
